import React from 'react';
import PropTypes from 'prop-types';
import { getClassName } from '../../../utils/ui/classes';

const sizes = {
  xsmall: 'xsmall',
  small: 'small',
  normal: 'normal',
};

function getColorClass({ color, disabled, active }) {
  if (disabled) {
    return ['bg-gray-300', 'text-gray-700', 'hover:bg-gray-100'];
  }

  const ringClass = {
    'ring-2': active,
    'ring-offset-2': active,
  };

  switch (color) {
    case 'blue':
      return [
        {
          'bg-blue-50': !active,
          'bg-blue-600': active,
          'ring-blue-500': active,
        },
        'text-blue-700',
        'hover:bg-blue-100',
        ringClass,
      ];
    case 'red':
      return [
        { 'bg-red-50': !active, 'bg-red-600': active, 'ring-red-500': active },
        'text-red-700',
        'hover:bg-red-100',
        'focus:ring-red-500',
        ringClass,
      ];
    case 'yellow':
      return [
        {
          'bg-yellow-50': !active,
          'bg-yellow-600': active,
          'ring-yellow-500': active,
        },
        'text-yellow-600',
        'hover:bg-yellow-100',
        'focus:ring-yellow-500',
        ringClass,
      ];
    case 'brand':
      return [
        {
          'bg-green': !active,
          'bg-green-600': active,
          'ring-green-cm': active,
        },
        'text-green-50',
        'hover:bg-green-600',
        'focus:ring-green-cm',
        ringClass,
      ];
    case 'green':
      return [
        {
          'bg-green-50': !active,
          'bg-green-600': active,
          'ring-green-500': active,
        },
        'text-green-700',
        'hover:bg-green-100',
        'focus:ring-green-500',
        ringClass,
      ];
    default:
      return [
        {
          'bg-gray-200': !active,
          'bg-gray-600': active,
          'ring-gray-500': active,
        },
        'text-gray-700',
        'hover:bg-gray-300',
        'focus:ring-gray-500',
        ringClass,
      ];
  }
}

function getSizeClass(size = sizes.normal) {
  switch (size) {
    case sizes.xsmall:
      return ['w-5', 'h-5'];
    case sizes.small:
      return ['w-8', 'h-8'];
    default:
      return ['w-10', 'h-10'];
  }
}

export function getCircleButtonClass({
  color,
  disabled,
  size = sizes.normal,
  className,
  active,
}) {
  return getClassName(
    {
      'px-2': true,
      'rounded-full': true,
      'flex-grow-0': true,
      'flex-shrink-0': true,
      'pointer-events-none': disabled,
      'pointer-events-auto': !disabled,
      'outline-none': true,
      flex: true,
      'items-center': true,
      'justify-center': true,
      'focus:ring-2': true,
      'focus:outline-none': true,
      'focus:ring-offset-2': true,
    },
    ...getColorClass({ color, disabled, active }),
    ...getSizeClass(size),
    className,
  );
}
const CircleButton = ({
  color,
  title,
  size,
  id,
  className,
  disabled,
  children,
  onClick,
  active,
  dataTestId,
}) => {
  function handleClick(e) {
    e.preventDefault();
    onClick(e);
  }

  return (
    <button
      id={id}
      type="button"
      onClick={handleClick}
      disabled={disabled}
      title={title}
      data-testid={dataTestId || 'circleButton'}
      className={getCircleButtonClass({
        color,
        size,
        className,
        disabled,
        active,
      })}
    >
      {children}
    </button>
  );
};

CircleButton.propTypes = {
  color: PropTypes.string.isRequired,
  size: PropTypes.oneOf(Object.keys(sizes)),
  title: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.any,
  id: PropTypes.string,
  active: PropTypes.bool,
  dataTestId: PropTypes.string,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
};

CircleButton.defaultProps = {
  size: sizes.normal,
  title: '',
  disabled: false,
  active: false,
};

export default CircleButton;
